import { Box ,Button,Typography, useTheme, useMediaQuery } from '@mui/material';
import * as React from 'react';
import { useLocation, useNavigate, useParams, NavigateFunction } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { League, LeagueGame, Player } from '../../../sdk/model';
import { leagueGameApi } from '../../../adapters/APIExporter';
import MatchupStarters from './MatchupPlayers';
import { getUsersTeamFromLeague } from '../../util/TeamUtils';
import { usePlayerDataLoadFlat } from '../../util/PlayerDataUtil';
import { loadGameRosterFromQuickstats } from '../../util/GameUtil';
// import confetti from 'canvas-confetti';
import { useDispatch } from 'react-redux';

function MatchupView({league}) {
  // TODO: Make matchup also take in user
  var {leagueId, week: paramsWeek, season: paramsSeason, teamId: paramsTeamId, id} = useParams();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const user = useAppSelector((state) => state.user);
  const myTeam = getUsersTeamFromLeague(league, user?.id);
  const location = useLocation();
  
  const [game, setGame] = React.useState<LeagueGame | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [games, setGames] = React.useState<LeagueGame[]>([]); 
  // TODO: If the game exist (i.e. after a reload, then we want to set the week and season to the game's week and season)
  const [week, setWeek] = React.useState(paramsWeek || (id ? undefined : league.currentSeason?.currentWeek));
  const [season, setSeason] = React.useState(paramsSeason || league.currentSeason?.seasonYear);
  const [teamId, setTeamId] = React.useState(paramsTeamId || myTeam?.id);
  
  // Track when we're updating state programmatically vs from URL changes
  const skipAPICallRef = React.useRef<boolean>(false);

  const playerMap :  Map<string | undefined, Player | undefined> = usePlayerDataLoadFlat();

  const navigate = useNavigate();

  // This is required for navigation on page using the matchup links
  React.useEffect(() => {
    if (paramsWeek) setWeek(paramsWeek);
    if (paramsSeason) setSeason(paramsSeason);
  }, [paramsWeek, paramsSeason]);

  // Load game from backend (should this be saved in a slice state?)
  React.useEffect(() => {
    // If this is a programmatic update, skip the API call
    if (skipAPICallRef.current) {
      skipAPICallRef.current = false;
      setIsLoading(false);
      return;
    }
    
    if (leagueId) {
      // Check if we already have the game we need
      if (id) {
        // If we have an ID and the current game matches, don't reload
        if (game && game.id === id) {
          setIsLoading(false);
          return;
        }
      } else if (week && season) {
        // If we're looking for a week/season game, check if current game matches
        if (game && game.weekNumber === Number(week) && game.seasonYear === Number(season)) {
          setIsLoading(false);
          return;
        }
        // Also check if we have games loaded for this week already
        const existingGame = games.find(g => g.weekNumber === Number(week) && g.seasonYear === Number(season));
        if (existingGame && !teamId) {
          setGame(existingGame);
          setIsLoading(false);
          return;
        }
      }
      
      setIsLoading(true);
      getLeagueGame(leagueId, week, season, teamId, id, setGame, setGames, setWeek, navigate, setIsLoading);
    }
  }, [leagueId, week, season, teamId, id, navigate, game?.id, game?.weekNumber, game?.seasonYear, games])
  
  const nflGames = useAppSelector((state) => state.nflGameDataLoad );
  const nflGameMap = React.useMemo(() => 
    new Map(nflGames.map(nflGame => [nflGame.id!, nflGame])), 
    [nflGames]
  );

  // Memoize the game scoring function to prevent unnecessary recalculations
  const scoredGame = React.useMemo(() => {
    if(!game || game.finalScore) {
      return game;
    }

    // Check if the game is in the future
    if(game && game.seasonYear === league.currentSeason?.seasonYear && game.weekNumber! > league.currentSeason?.currentWeek) {
      // If so we just show the current roster with a 0 score
      return game;
    }

    // Only load scores for current week games
    if(game.seasonYear !== league.currentSeason?.seasonYear || game.weekNumber !== league.currentSeason?.currentWeek) {
      return game;
    }

    console.debug("Loading game roster...")
    return loadGameRosterFromQuickstats(league, game, playerMap, nflGameMap);
  }, [game, league.currentSeason?.seasonYear, league.currentSeason?.currentWeek, playerMap, nflGameMap]);

  // Update game state only when scoredGame changes
  React.useEffect(() => {
    if (scoredGame !== game) {
      setGame(scoredGame);
    }
  }, [scoredGame, game]);

  // Update season and week when game changes (only if different)
  React.useEffect(() => {
    if(game && game.seasonYear && game.weekNumber) {
      if(season !== game.seasonYear) setSeason(game.seasonYear);
      if(week !== game.weekNumber) setWeek(game.weekNumber);
    }
  }, [game?.seasonYear, game?.weekNumber, season, week]);

  if (!league) {
    return <p>Loading...</p>;
  } 
  
  if (isLoading) {
    return <p>Loading...</p>;
  } 
  else if (game && !game.homeTeamRoster) {
    return <p>Loading Score...</p>;
  } 
  else if (!game) {
    return (
      <Box>
        <MatchupLinks seasonYear={season} currentWeek={week} league={league} setGame={setGame} games={games} currentGameId={undefined} setWeek={setWeek} setSeason={setSeason} skipAPICallRef={skipAPICallRef} />
        <Typography>Game not found</Typography>
      </Box>
    );
  }

  // Shoot some confetti if the user won
  // if(!game.loading && game.winningTeamId === myTeam?.id) {
  //   confetti({
  //     particleCount: 100,
  //     spread: 70,
  //     origin: { y: 0.6 }
  //   });
  // }

  return (
    <Box sx={{ padding: { xs: '8px', sm: '16px' } }}>
      <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
        <Box sx={{ marginBottom: { xs: '12px', sm: '16px' } }}>
          <Typography 
            variant="h4" 
            align="center" 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              marginBottom: { xs: '4px', sm: '8px' }
            }}
          >
            Week {game.weekNumber}
          </Typography>
          <Typography 
            variant="body2" 
            align="center"
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {game.seasonYear}
          </Typography>
        </Box>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <MatchupLinks seasonYear={game?.seasonYear} currentWeek={week} league={league} setGame={setGame} games={games} currentGameId={game.id} setWeek={setWeek} setSeason={setSeason} skipAPICallRef={skipAPICallRef} />
        </Box>
      </Box>
      <Box sx={{ marginTop: { xs: '16px', sm: '24px' } }}>
        <MatchupStarters league={league!} game={game} />
      </Box>
    </Box>
  );

}


function MatchupLinks({ seasonYear, league, setGame, currentWeek, games, currentGameId, setWeek, setSeason, skipAPICallRef }: { 
  seasonYear: number | undefined, 
  league: League, 
  setGame: React.Dispatch<React.SetStateAction<LeagueGame | undefined>>, 
  currentWeek: number | undefined, 
  games: LeagueGame[], 
  currentGameId: string | undefined,
  setWeek: React.Dispatch<React.SetStateAction<number | undefined>>,
  setSeason: React.Dispatch<React.SetStateAction<number | undefined>>,
  skipAPICallRef: React.MutableRefObject<boolean>
}) {

  // Navigator/Dispatch
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const maxWeek = Number(seasonYear) === league.currentSeason?.seasonYear ? league.currentSeason?.currentWeek || 17 : 17;
  const user = useAppSelector((state) => state.user);
  const myTeam = getUsersTeamFromLeague(league, user?.id);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      gap: 1,
      px: { xs: 1, sm: 2 }
    }}>
      {/* Week Navigation - Horizontal scroll on mobile */}
      <Box 
        className="matchup-links"
        sx={{
          display: 'flex',
          justifyContent: isSmallMobile ? 'flex-start' : 'center',
          overflowX: isSmallMobile ? 'auto' : 'visible',
          flexWrap: isSmallMobile ? 'nowrap' : 'wrap',
          width: '100%',
          maxWidth: { xs: '100vw', sm: 'none' },
          gap: { xs: 0.5, sm: 1 },
          padding: { xs: '4px', sm: '8px' },
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {Array.from({ length: maxWeek }, (_, i) => (
          <Button 
            key={i+1}
            className="matchup-link"
            disabled={currentWeek == (i+1)}
            size={isSmallMobile ? 'small' : 'medium'}
            sx={{
              minWidth: { xs: '28px', sm: '36px' },
              height: { xs: '32px', sm: '36px' },
              padding: { xs: '2px 4px', sm: '4px 8px' },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              fontWeight: 'bold',
              flexShrink: 0,
              borderRadius: 1,
              ...(currentWeek == (i+1) && {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:disabled': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  opacity: 1
                }
              })
            }}
            onClick={() => {
              if (currentWeek == (i+1)) {
                return;
              }
              const weekNumber = i + 1;
              
              // Check if we already have games for the target week
              const targetWeekGame = games.find(game => game.weekNumber === weekNumber);
              
              if (targetWeekGame) {
                // We have games for this week already loaded
                const userGame = games.find(game => 
                  game.weekNumber === weekNumber && 
                  (game.homeTeamId === myTeam?.id || game.awayTeamId === myTeam?.id)
                );
                
                const gameToSet = userGame || targetWeekGame;
                
                // Set flag to skip API call and update state + URL
                skipAPICallRef.current = true;
                setGame(gameToSet);
                setWeek(weekNumber);
                setSeason(seasonYear);
                navigate(`/league/${league.id}/matchup/id/${gameToSet.id}`);
                return;
              }
              
              // Navigate to new week - this will trigger useEffect to load new games
              setWeek(weekNumber);
              setSeason(seasonYear);
              navigate(`/league/${league.id}/matchup/${weekNumber}/${seasonYear}`);
            }}
          >
            {i+1}
          </Button>
        ))}
      </Box>
      
      {/* Game Links - Centered and compact */}
      <Box 
        className="game-links"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 1 },
          maxWidth: { xs: '200px', sm: 'none' },
          padding: { xs: '4px', sm: '8px' }
        }}
      >
        {games.length > 0 ? games.map(game => 
          <Button 
            key={game.id} 
            className="matchup-link" 
            disabled={currentGameId === game.id} 
            size={isSmallMobile ? 'small' : 'medium'}
            sx={{
              minWidth: { xs: '24px', sm: '32px' },
              height: { xs: '28px', sm: '32px' },
              padding: { xs: '2px', sm: '4px' },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              borderRadius: '50%',
              ...(currentGameId === game.id && {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:disabled': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  opacity: 1
                }
              })
            }}
            onClick={() => {
              setGame(game)
              navigate(`/league/${league.id}/matchup/id/${game.id}`)
            }}
          >
            •
          </Button>
        ) : (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            No other games this week
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function getLeagueGame(leagueId: string, week: number | undefined, season: number | undefined, teamId: string | undefined, id: string | undefined, 
  setGame: React.Dispatch<React.SetStateAction<LeagueGame | undefined>>, setGames: React.Dispatch<React.SetStateAction<LeagueGame[]>>,
  setWeek: React.Dispatch<React.SetStateAction<number | undefined>>, navigate: NavigateFunction, setIsLoading: React.Dispatch<React.SetStateAction<boolean>>) {
  console.log("Getting game by week: " + week + " season: " + season + " league: " + leagueId + " team: " + teamId)
  if (id && !week) {
    leagueGameApi.getGame(id).then(response => {
      const game = response.data;
      setGame(game);
      const weekNumber = game.weekNumber;
      setWeek(weekNumber);
      
      // Also load all games for this week to populate the game links
      if (game.seasonYear && weekNumber) {
        leagueGameApi.getGameByWeek(leagueId, weekNumber, game.seasonYear).then(weekResponse => {
          setGames(weekResponse.data);
        }).catch(error => {
          console.error("Error loading week games:", error);
        });
      }
      
      setIsLoading(false);
    }).catch(error => {
      console.error("Error loading game:", error);
      setIsLoading(false);
    });
  } else if (week && season) {
    leagueGameApi.getGameByWeek(leagueId, Number(week), Number(season)).then(response => {
      setGames(response.data)
      if (id) {
        setGame(response.data.find(game => id === game.id));
      }
      if (teamId) {
        const gameId = response.data.find(game => teamId === game.awayTeamId || teamId === game.homeTeamId)?.id;
        if (gameId) {
          navigate(`/league/${leagueId}/matchup/id/${gameId}`)
        }
      }
      setIsLoading(false);
    }).catch(error => {
      console.error("Error loading games:", error);
      setIsLoading(false);
    });
  } else {
    setIsLoading(false);
  }
}

export default MatchupView;
